import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	CarrierFeedback,
	CarrierNotes,
	CarrierNotesListResponse,
	GET_CARRIER_BUSINESS_NAME,
	RestService,
	User,
} from 'cargo-chief-commons';
import { NotesService } from '@service/notesService/notes.service';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { CarrierNameService } from '@service/carrier-name/carrier-name.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared/base/base.component';
import { FeedbackInformation } from '@notes/note/note.component';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';

export enum NoteType {
	CARRIER,
	PROJECT,
	LOAD,
}

interface CarrierNoteData {
	active: boolean;
	base: CarrierNotes;
	feedback: any;
	noteEditAndDeleteEnabled: boolean;
	user: User;
}

@Component({
	selector: 'app-note-list',
	templateUrl: './note-list.component.html',
})
export class NoteListComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
	@Input() noteType: NoteType = NoteType.CARRIER;
	@Input() id: string = null;
	@Input() supportId: string = null;
	@Input() name: string = null;
	// CN-8618: Load notes - notes are scoped to a load via loadId, optionally
	// tagged with the carrier of a selected bid row via carrierId.
	@Input() loadId: string = null;
	@Input() carrierId: string = null;
	// F7e: hide the inline "add note" input when notes are created elsewhere
	// (the bid-row Log Call / contact actions). Defaults true to preserve the
	// standard carrier/project notes UX everywhere else.
	@Input() showAddNote: boolean = true;
	@Input() feedbackInformation: FeedbackInformation;
	@Input() showFeedback: boolean;
	@Input() defaultFeedback: CarrierFeedback[] | null;
	@Input() feedbackClicked: EventEmitter<any> | null;
	@Input() noteCount: number;
	@Input() manualNoteCount = false;
	@Input() useExactMatch = true;
	@Input() edit = true;
	@Input() truckLaneId:string = null;
	@Input() origin:string = null;
	@Input() destination:string = null;
	// CN-8312: Input to trigger a refresh of notes when changed
	@Input() refreshTrigger: number = 0;

	@Output() noteCountUpdated: EventEmitter<number> = new EventEmitter<number>();
	@Output() noteAdded: EventEmitter<any> = new EventEmitter();

	page = 0;
	size = 100;
	isLoading: boolean;
	hasNext = true;

	carrierNotes: Map<string, CarrierNoteData> = new Map();
	hasActiveEdit: string | undefined | null = null;
	// CN-8645: carrierId -> business name, so a load note can show which carrier
	// it's from when no bid row is selected (the note only carries carrierId).
	carrierNames: Map<string, string> = new Map();

	constructor(
		private ref: ChangeDetectorRef,
		private urlService: UrlService,
		private restService: RestService,
		private notesService: NotesService,
		private utilService: UtilService,
		private cargochief: Cargochief,
		private carrierNameService: CarrierNameService
	) {
		super();
	}

	ngOnInit(): void {
		if (this.showFeedback === undefined) {
			this.showFeedback = this.noteType === NoteType.CARRIER;
		}

		this.feedbackClicked?.pipe(takeUntil(this.isDestroyed$)).subscribe((id) => {
			if (id) {
				this.makeActive(id);
			}
		});

		if (this.defaultFeedback?.length) {
			this.loadFeedback(this.defaultFeedback, false);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.name?.previousValue !== changes?.name?.currentValue) {
			this.carrierNotes = new Map();
			this.hasActiveEdit = null;
			this.hasNext = true;
			this.page = 0;
		}

		// CN-8618: Reset state when the load changes so notes re-fetch for the new load.
		if (changes?.loadId && !changes.loadId.firstChange) {
			this.carrierNotes = new Map();
			this.hasActiveEdit = null;
			this.hasNext = true;
			this.page = 0;
		}

		// CN-8312: Reset state when refreshTrigger changes to allow re-fetch
		if (changes?.refreshTrigger && !changes.refreshTrigger.firstChange) {
			this.carrierNotes = new Map();
			this.hasActiveEdit = null;
			this.hasNext = true;
			this.page = 0;
		}

		if (this.defaultFeedback?.length) {
			this.loadFeedback(this.defaultFeedback, false);
		}

		// CN-8312: Also refresh when refreshTrigger changes
		// CN-8618: Load notes refetch when loadId changes
		if (changes?.id || changes?.supportId || changes?.refreshTrigger || changes?.loadId) {
			this.fetchNotes();
		}

		// CN-8641: selecting/clearing a carrier doesn't refetch (load notes are already
		// loaded), but the tab badge must reflect the carrier-filtered count, so re-emit.
		if (changes?.carrierId && !changes.carrierId.firstChange && !this.manualNoteCount) {
			this.noteCountUpdated.emit(this.emittedNoteCount());
		}

		if (
			(changes?.id || changes?.supportId) &&
			this.noteType === NoteType.CARRIER &&
			this.id &&
			!this.name
		) {
			this.restService
				.POST(this.urlService.getCarriersByIds(), { carrierIDs: [this.id] })
				.pipe(takeUntil(this.isDestroyed$))
				.subscribe((result) => {
					const carrier = result.data.content[0];
					this.name = GET_CARRIER_BUSINESS_NAME(carrier?.businessName, carrier?.doingBusinessAs);
				});
		}
	}

	fetchNotes(): void {
		if (!this.hasNext) {
			return;
		}

		this.isLoading = true;

		const url = this.urlService.listCarrierNotesByPost(this.page, this.size);

		const payload = {
			projectId: this.noteType === NoteType.PROJECT ? this.id : '',
			carrierIds: this.noteType === NoteType.CARRIER ? [this.id] : [],
			// CN-8618: Load notes are listed for the whole load, regardless of carrier.
			...(this.noteType === NoteType.LOAD && this.loadId ? { loadId: this.loadId } : {}),
		};

		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(response: CarrierNotesListResponse) => {
					this.isLoading = false;

					let users;

					try {
						users = JSON.parse(
							this.cargochief.getItem(CARGOCHIEF_KEYS.ACCOUNT_USERS)
						) as Array<User>;
					} catch (ignored) {}

					const mappedResponse = response.data.map(
						(data) =>
							<CarrierNoteData>{
								active: false,
								base: data,
								feedback: null,
								noteEditAndDeleteEnabled:
									this.utilService.isUserRoleAdminOrOwnerOnly() ||
									data.userId === this.cargochief.USER_ID,
								user: users?.find((user) => Number(user.id) === Number(data.userId)),
							}
					);

					const newMap = new Map();
					if (this.carrierNotes.size) {
						for (const [key, value] of this.carrierNotes.entries()) {
							newMap.set(key, value);
						}
					}

					mappedResponse.forEach((note) => {
						if (!this.supportId || this.checkSupportID(note.base)) {
							newMap.set(note.base.id, note);
						}
					});

					this.carrierNotes = newMap;
					this.resolveCarrierNames();
					if (this.defaultFeedback?.length) {
						this.loadFeedback(this.defaultFeedback, false);
					}
					this.hasNext = response.nextPage;

					if (!this.manualNoteCount) {
						this.noteCount = this.carrierNotes.size;
						this.noteCountUpdated.emit(this.emittedNoteCount());
					}
				},
				(error) => {
					this.isLoading = false;
					this.utilService.showSnackbar(
						error.data || 'Unable to load notes...',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	addNote(note: CarrierNotes): void {
		const newMap = new Map();

		newMap.set(note.id, {
			active: false,
			base: note,
			feedback: null,
			noteEditAndDeleteEnabled: true,
		});

		for (const [key, value] of this.carrierNotes.entries()) {
			newMap.set(key, value);
		}

		this.carrierNotes = newMap;
		this.resolveCarrierNames();
		this.noteCount += 1;
		this.noteCountUpdated.emit(this.emittedNoteCount());
		if (this.defaultFeedback) {
			this.loadFeedback(this.defaultFeedback, false);
		}
		this.noteAdded.emit(note);
		this.hasActiveEdit = null;
	}

	deleteNote(noteId: string) {
		this.carrierNotes.delete(noteId);
		this.noteCount -= 1;
		this.noteCountUpdated.emit(this.emittedNoteCount());
	}

	// CN-8645: resolve carrier business names for the loaded notes so each load note
	// can show which carrier it belongs to. Only load notes mix carriers across one
	// list; carrier/project notes already render their own carrier context.
	private resolveCarrierNames(): void {
		if (!this.isLoadType()) {
			return;
		}
		const ids = Array.from(this.carrierNotes.values()).map((entry) => entry?.base?.carrierId);
		if (!ids.some(Boolean)) {
			return;
		}
		this.carrierNameService
			.resolveNames(ids)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((names) => (this.carrierNames = names));
	}

	// CN-8645: the business name for a note's carrier (empty when unknown/non-load).
	public carrierNameFor(carrierId: string | null | undefined): string {
		return carrierId ? this.carrierNames.get(String(carrierId)) || '' : '';
	}

	// CN-8641: the count surfaced to the tab badge. When scoped to a selected carrier
	// (Load notes with carrierId), emit the carrier-filtered count so the badge matches
	// what's displayed; otherwise the full note count (unchanged for project/lane usages).
	private emittedNoteCount(): number {
		if (!this.carrierId) {
			return this.noteCount;
		}
		let count = 0;
		this.carrierNotes.forEach((entry) => {
			const base = entry?.base;
			const laneOk = this.truckLaneId === null || base?.truckLaneId === this.truckLaneId;
			if (base?.carrierId === this.carrierId && laneOk) {
				count++;
			}
		});
		return count;
	}

	loadFeedback(feedbacks: CarrierFeedback[], newFeedback: boolean): void {
		const newMap = new Map();

		if (newFeedback) {
			this.defaultFeedback.unshift(feedbacks[0]);
		}

		for (const [key, value] of this.carrierNotes.entries()) {
			newMap.set(key, {
				...value,
				feedback: feedbacks.find((feedback) => feedback.noteId === key) || value.feedback,
			});
		}

		this.carrierNotes = newMap;
	}

	makeActive(index: string | undefined | null): void {
		this.hasActiveEdit = index;

		const newMap = new Map();
		for (const [key, value] of this.carrierNotes.entries()) {
			newMap.set(key, {
				active: index === key,
				base: value.base,
				feedback: value.feedback,
				noteEditAndDeleteEnabled: value.noteEditAndDeleteEnabled,
			});
		}

		this.carrierNotes = newMap;
	}

	onScroll(): void {
		if (this.hasNext) {
			this.page++;
			this.fetchNotes();
		}
	}

	getCarrierNoteKeys(): string[] {
		return Array.from(this.carrierNotes.keys());
	}

	public getCarrierNotesList(): Array<CarrierNotes> {
		return Array.from(this.carrierNotes.values()).map((note) => note.base);
	}

	private checkSupportID(note: CarrierNotes): boolean {
		return this.noteType === NoteType.CARRIER
			? note.projectId === this.supportId || note.projectId === null
			: note.carrierId === this.supportId;
	}

	public isProjectType(): boolean {
		return this.noteType === NoteType.PROJECT;
	}

	public isLoadType(): boolean {
		return this.noteType === NoteType.LOAD;
	}

	// CN-8618: Load notes are scoped to a load and do not require a carrier name
	// to render the add/list panel; carrier/project notes still gate on `name`.
	public canRenderNotes(): boolean {
		return this.isLoadType() ? !!this.loadId : !!this.name;
	}
}
